import { Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Subject, finalize, first, takeUntil } from "rxjs";
import { IApiResponseForArray } from "src/app/shared/interfaces/instance.interface";
import { InstanceService } from "src/app/shared/services/instance.service";
import { InvitationCreationErrorCode } from "../../domain/interfaces/invitation.interface";
import { InvitationService } from "../../domain/services/invitation.service";
import { OrganizationService } from "../../domain/services/organization.service";

const SUPER_ADMIN_ROLE = "SuperAdmin";

@Component({
  selector: "app-invite-member-dialog",
  templateUrl: "./invite-member-dialog.component.html",
  styleUrls: ["./invite-member-dialog.component.scss"],
})
export class InviteMemberDialogComponent implements OnInit, OnDestroy {
  public roles: string[] = [];
  public isSuccess = false;
  public successEmail = "";

  /**
   * Emitted each time an invitation is successfully sent, rather than only once when the dialog
   * closes: the organization's table underneath stays visible while the dialog is open, and
   * should be kept up to date as invitations are sent, not just after the dialog is dismissed.
   */
  @Output() memberInvited = new EventEmitter<void>();

  public emailAlreadyMember = false;
  public invitationAlreadyPending = false;
  public superAdminOnly = false;
  protected onDestroy$ = new Subject<void>();

  public inviteForm = this.fb.nonNullable.group({
    email: ["", [Validators.required, Validators.pattern(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)]],
    role: ["", Validators.required],
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly instanceService: InstanceService,
    private readonly invitationService: InvitationService,
    private readonly organizationService: OrganizationService,
    private readonly translateService: TranslateService,
    public readonly dialogRef: MatDialogRef<InviteMemberDialogComponent>,
    private readonly snackBar: MatSnackBar
  ) {}

  /**
   * Loads the roles that can be given to the invited member, closing the dialog if they can't
   * be recovered. The SuperAdmin role is only offered to a SuperAdmin, as only they can invite one.
   */
  ngOnInit(): void {
    this.instanceService
      .getRolesWithPermissions()
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe({
        next: (res: IApiResponseForArray) => {
          this.roles = res.roles.map((r) => r.name).filter((name) => name !== SUPER_ADMIN_ROLE || this.canInviteSuperAdmin);
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
          this.dialogRef.close();
        },
      });
  }

  /**
   * Emits onDestroy$ to terminate all active subscriptions.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Whether the user can invite a member as a SuperAdmin, i.e. is a SuperAdmin themselves (only a
   * SuperAdmin can list every organization).
   */
  get canInviteSuperAdmin(): boolean {
    return this.organizationService.canGetAllOrganizations();
  }

  /**
   * Validates and sends the invitation, showing a success indication on completion or surfacing
   * field-specific errors when the email is already a member or already has a pending invitation,
   * or when the role can only be given by a SuperAdmin.
   */
  submit(): void {
    this.isSuccess = false;

    if (this.inviteForm.invalid) {
      this.inviteForm.markAllAsTouched();
      return;
    }

    this.inviteForm.disable();
    const { email, role } = this.inviteForm.getRawValue();

    this.invitationService
      .inviteToOrg(email, role)
      .pipe(
        first(),
        takeUntil(this.onDestroy$),
        finalize(() => this.inviteForm.enable())
      )
      .subscribe({
        next: () => {
          this.successEmail = email;
          this.isSuccess = true;
          this.emailAlreadyMember = false;
          this.invitationAlreadyPending = false;
          this.superAdminOnly = false;
          this.inviteForm.reset();
          this.memberInvited.emit();
        },
        error: (err) => {
          if (err.status === 409 && err.error === InvitationCreationErrorCode.EMAIL_ALREADY_MEMBER) {
            this.emailAlreadyMember = true;
          } else if (err.status === 409 && err.error === InvitationCreationErrorCode.INVITATION_ALREADY_PENDING) {
            this.invitationAlreadyPending = true;
          } else if (err.status === 403 && err.error === InvitationCreationErrorCode.SUPER_ADMIN_ONLY) {
            this.superAdminOnly = true;
          } else {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
          }
        },
      });
  }
}
